// src/pages/RestaurantOrderPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { PageHeader, RowActions, StatusTag, TabBar, fmtDateTime, tablePagination, useFlatPage } from "../components/ui";
import "./RestaurantOrderPage.css";
import CheckOutInvoice from "../components/CheckOutInvoice";
import EditOrderModal from "./EditOrderModal";
import { useAuth } from "../context/AuthContext";

import {
  Typography,
  Table,
  Space,
  Button,
  Modal,
  Input,
  Alert,
} from "antd";
import {
} from "@ant-design/icons";
import { IconRefresh } from "../components/ui/icons";
import notify from "../utils/notify";

const { Title, Text } = Typography;

/* ---------------- helpers ---------------- */
const asNum = (v) => Number(v ?? 0);
const money = (n, ccy = "BWP") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(
    asNum(n)
  );
const pick = (obj, keys) => {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

// Normalize an order object (handles PascalCase/camelCase)
const normalizeOrder = (o) => {
  if (!o) return null;
  return {
    orderId: pick(o, ["orderId", "OrderId"]),
    roomNumber: pick(o, ["roomNumber", "RoomNumber"]),
    description: pick(o, ["description", "Description"]) ?? "",
    amount: asNum(pick(o, ["amount", "Amount"])) || 0,
    createdDate: pick(o, ["createdDate", "CreatedDate"]),
    createdBy: pick(o, ["createdBy", "CreatedBy"]) ?? "System",
    status: pick(o, ["status", "Status"]) ?? "Pending",

    // fulfilment fields
    deliveryMethod:
      pick(o, ["deliveryMethod", "DeliveryMethod"]) || "RoomService", // RoomService | Collection | DineIn
    pickupLocation: pick(o, ["pickupLocation", "PickupLocation"]) || "",
    tableReference: pick(o, ["tableReference", "TableReference"]) || "",

    // decision fields
    rejectionReason:
      pick(o, ["rejectionReason", "RejectionReason", "rejectReason"]) || "",
    acceptedDate: pick(o, ["acceptedDate", "AcceptedDate"]),
    rejectedDate: pick(o, ["rejectedDate", "RejectedDate"]),
  };
};

const statusTag = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "pending") return <StatusTag status="pending">Pending</StatusTag>;
  if (s === "accepted") return <StatusTag status="confirmed">Accepted</StatusTag>;
  if (s === "processing" || s === "processed" || s === "in progress" || s === "ready")
    return <StatusTag status="checkedin">{s === "ready" ? "Ready" : "Processing"}</StatusTag>;
  if (s === "rejected") return <StatusTag status="cancelled">Rejected</StatusTag>;
  if (s === "completed" || s === "closed") return <StatusTag status="available">Completed</StatusTag>;
  return <StatusTag status={s}>{status}</StatusTag>;
};

const FulfilmentCell = ({ order }) => {
  const method = order.deliveryMethod;
  if (method === "Collection") {
    return (
      <Space direction="vertical" size={0}>
        <span className="ro-kind">Collection</span>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {order.pickupLocation ? `Pickup @ ${order.pickupLocation}` : "Pickup @ counter"}
        </Text>
      </Space>
    );
  }
  if (method === "DineIn") {
    return (
      <Space direction="vertical" size={0}>
        <span className="ro-kind">Dine-in</span>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {order.tableReference ? `Table: ${order.tableReference}` : "No table reserved yet"}
        </Text>
      </Space>
    );
  }
  // RoomService default
  return (
    <Space direction="vertical" size={0}>
      <span className="ro-kind">Room service</span>
      {order.roomNumber ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Deliver to Room {order.roomNumber}
        </Text>
      ) : null}
    </Space>
  );
};

/* ---- AntD modals ---- */
const ReserveTableModal = ({
  open,
  order,
  tableRef,
  setTableRef,
  onCancel,
  onSubmit,
  submitting,
}) => {
  return (
    <Modal
      open={open}
      onCancel={onCancel}
      onOk={onSubmit}
      okText="Reserve table"
      confirmLoading={submitting}
      title="Reserve a table"
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        <Text type="secondary">
          Dine-in reservation for {order?.roomNumber ? `Room ${order.roomNumber}` : "guest"}.
        </Text>
        <Text strong>Table number or reference</Text>
        <Input
          placeholder="e.g. 5 / Window area"
          value={tableRef}
          onChange={(e) => setTableRef(e.target.value)}
          disabled={submitting}
        />
      </Space>
    </Modal>
  );
};

const RejectOrderModal = ({
  open,
  order,
  reason,
  setReason,
  onCancel,
  onSubmit,
  submitting,
}) => {
  return (
    <Modal
      open={open}
      onCancel={onCancel}
      onOk={onSubmit}
      okText="Reject order"
      okButtonProps={{ danger: true }}
      confirmLoading={submitting}
      title="Reject order"
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        <Text type="secondary">
          Provide a reason (e.g., restaurant closed, item unavailable).
        </Text>
        <Text strong>Reason</Text>
        <Input.TextArea
          rows={4}
          placeholder="Restaurant is closed at this time."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
        />
      </Space>
    </Modal>
  );
};

const RestaurantOrderPage = () => {
  useFlatPage();
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  const [activeKey, setActiveKey] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");

  // cache per tab
  const [cache, setCache] = useState({
    pending: null,
    accepted: null,
    processing: null,
    rejected: null,
    completed: null,
  });

  // Edit modal
  const [isEditOrderModalOpen, setIsEditOrderModalOpen] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState(null);

  // Reserve table
  const [isReserveOpen, setIsReserveOpen] = useState(false);
  const [orderToReserve, setOrderToReserve] = useState(null);
  const [reserveTableRef, setReserveTableRef] = useState("");
  const [reserveSubmitting, setReserveSubmitting] = useState(false);

  // Reject order
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [orderToReject, setOrderToReject] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectSubmitting, setRejectSubmitting] = useState(false);


  const handleEditOrder = (order) => {
    setOrderToEdit(order);
    setIsEditOrderModalOpen(true);
  };

  const refreshTab = async (key = activeKey) => {
    if (!hotelId) {
      setErrorMsg("Hotel ID is missing. Please sign in again.");
      return;
    }

    setErrorMsg("");
    setLoading(true);
    try {
      let data = [];
      if (key === "pending") {
        const res = await axiosInstance.get(`/hotels/${hotelId}/RestaurantOrders/activeOrders`);
        data = (res.data || []).map(normalizeOrder);
      } else if (key === "completed") {
        const res = await axiosInstance.get(`/hotels/${hotelId}/RestaurantOrders/closedOrders`);
        data = (res.data || []).map(normalizeOrder);
      } else {
        // accepted / processing / rejected
        try {
          const res = await axiosInstance.get(
            `/hotels/${hotelId}/RestaurantOrders/byStatus?status=${encodeURIComponent(key)}`
          );
          data = (res.data || []).map(normalizeOrder);
        } catch {
          data = [];
          setErrorMsg(
            "Add: GET /api/hotels/{hotelId}/RestaurantOrders/byStatus?status=" + key
          );
        }
      }
      setOrders(data);
      setCache((prev) => ({ ...prev, [key]: data }));
    } catch (err) {
      console.error("Fetch failed:", err);
      setOrders([]);
      setErrorMsg(
        err?.response?.data?.message ||
          "Failed to fetch orders for this status. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const onTabChange = async (key) => {
    setActiveKey(key);
    if (cache[key] !== null) {
      setOrders(cache[key] || []);
      return;
    }
    await refreshTab(key);
  };

  useEffect(() => {
    if (hotelId) {
      refreshTab("pending");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  const handleCloseOrder = async (orderId) => {
    if (!hotelId) {
      notify.error("Hotel ID is missing.");
      return;
    }
    try {
      await axiosInstance.post(`/hotels/${hotelId}/RestaurantOrders/CloseOrder/${orderId}`);
      notify.success("Order closed.");
      await refreshTab();
      setCache((prev) => ({ ...prev, completed: null }));
    } catch (err) {
      console.error("Error closing order:", err);
      notify.error("Failed to close order.");
    }
  };

  const handleAcceptOrder = async (orderId) => {
    if (!hotelId) {
      notify.error("Hotel ID is missing.");
      return;
    }
    try {
      await axiosInstance.post(`/hotels/${hotelId}/RestaurantOrders/AcceptOrder/${orderId}`);
      notify.success("Order accepted.");
      await refreshTab();
      setCache((prev) => ({ ...prev, accepted: null }));
    } catch (err) {
      console.error("Error accepting order:", err);
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        "Failed to accept order.";
      notify.error(msg);
    }
  };

  // Reject order
  const openRejectModal = (order) => {
    setOrderToReject(order);
    setRejectReason("");
    setIsRejectOpen(true);
  };

  const submitReject = async () => {
    if (!orderToReject || !hotelId) {
      notify.error("Hotel ID is missing.");
      return;
    }
    if (!rejectReason.trim()) {
      notify.warning("Please enter a reason.");
      return;
    }
    try {
      setRejectSubmitting(true);
      await axiosInstance.post(
        `/hotels/${hotelId}/RestaurantOrders/RejectOrder/${orderToReject.orderId}`,
        { reason: rejectReason.trim() }
      );
      setIsRejectOpen(false);
      setOrderToReject(null);
      notify.success("Order rejected.");
      await refreshTab();
      setCache((prev) => ({ ...prev, rejected: null }));
    } catch (err) {
      console.error("Reject order failed:", err);
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        "Failed to reject order.";
      notify.error(msg);
    } finally {
      setRejectSubmitting(false);
    }
  };

  // Reserve table
  const openReserveModal = (order) => {
    setOrderToReserve(order);
    setReserveTableRef(order?.tableReference || "");
    setIsReserveOpen(true);
  };

  const submitReserve = async () => {
    if (!orderToReserve || !hotelId) {
      notify.error("Hotel ID is missing.");
      return;
    }
    if (!reserveTableRef.trim()) {
      notify.warning("Enter a table number/reference.");
      return;
    }
    try {
      setReserveSubmitting(true);
      await axiosInstance.post(
        `/hotels/${hotelId}/RestaurantOrders/ReserveTable/${orderToReserve.orderId}`,
        { tableReference: reserveTableRef.trim() }
      );
      setIsReserveOpen(false);
      setOrderToReserve(null);
      notify.success("Table reserved.");
      await refreshTab();
    } catch (err) {
      console.error("Reserve table failed:", err);
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        "Failed to reserve table.";
      notify.error(msg);
    } finally {
      setReserveSubmitting(false);
    }
  };

  // Generic status update (PUT /RestaurantOrders/{id}/status; body is raw string)
  const updateStatus = async (order, nextStatus) => {
    if (!hotelId) {
      notify.error("Hotel ID is missing.");
      return;
    }
    try {
      await axiosInstance.put(
        `/hotels/${hotelId}/RestaurantOrders/${order.orderId}/status`,
        nextStatus,
        { headers: { "Content-Type": "application/json" } }
      );
      notify.success({
        message: "Order Updated",
        description: `Order #${order.orderId} marked as ${nextStatus}.`,
        placement: "bottomRight",
      });
      await refreshTab();
      setCache((prev) => ({
        ...prev,
        accepted: null,
        processing: null,
        rejected: null,
        completed: null,
        pending: null,
      }));
    } catch (err) {
      console.error("Status update failed:", err);
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        "Failed to update status.";
      notify.error(msg);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "Order",
        dataIndex: "orderId",
        key: "orderId",
        width: 100,
        render: (v) => <span className="ro-id">#{v}</span>,
        sorter: (a, b) => asNum(a.orderId) - asNum(b.orderId),
      },
      {
        title: "Room",
        dataIndex: "roomNumber",
        key: "roomNumber",
        width: 110,
        render: (v) => (v ? `Room ${v}` : "—"),
      },
      {
        title: "Fulfilment",
        key: "fulfilment",
        width: 200,
        render: (_, order) => <FulfilmentCell order={order} />,
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        ellipsis: true,
      },
      {
        title: "Total",
        dataIndex: "amount",
        key: "amount",
        width: 130,
        align: "right",
        render: (v) => <Text strong>{money(v, "BWP")}</Text>,
        sorter: (a, b) => asNum(a.amount) - asNum(b.amount),
      },
      {
        title: "Added",
        dataIndex: "createdDate",
        key: "createdDate",
        width: 170,
        render: (v) =>
          fmtDateTime(v),
        sorter: (a, b) => new Date(a.createdDate) - new Date(b.createdDate),
      },
      {
        title: "Processed by",
        dataIndex: "createdBy",
        key: "createdBy",
        width: 140,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 140,
        render: (s, order) => (
          <Space direction="vertical" size={0}>
            {statusTag(s)}
            {s === "Rejected" && order.rejectionReason ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Reason: {order.rejectionReason}
              </Text>
            ) : null}
          </Space>
        ),
      },
      {
        title: "",
        key: "actions",
        width: 220,
        align: "right",
        render: (_, order) => {
          const isPending = order.status === "Pending";
          const canReserve =
            order.deliveryMethod === "DineIn" &&
            !order.tableReference &&
            order.status !== "Closed" &&
            order.status !== "Completed";
          const canClose = order.status === "Pending" || order.status === "Accepted";

          return (
            <RowActions
              actions={[
                {
                  key: "accept",
                  label: "Accept",
                  icon: "accept",
                  showLabel: true,
                  tone: "primary",
                  hidden: !isPending,
                  onClick: () => handleAcceptOrder(order.orderId),
                },
                {
                  key: "reject",
                  label: "Reject",
                  icon: "reject",
                  danger: true,
                  hidden: !isPending,
                  onClick: () => openRejectModal(order),
                },
                {
                  key: "reserve",
                  label: "Reserve table",
                  icon: "reserve",
                  hidden: !canReserve,
                  onClick: () => openReserveModal(order),
                },
                { key: "edit", label: "Edit order", icon: "edit", onClick: () => handleEditOrder(order) },
                {
                  key: "close",
                  label: "Close order",
                  icon: "close",
                  hidden: !canClose,
                  confirm: "Close this order?",
                  onClick: () => handleCloseOrder(order.orderId),
                },
              ]}
              more={[
                { key: "Accepted", label: "Mark as accepted", onClick: () => updateStatus(order, "Accepted") },
                { key: "Processing", label: "Mark as processing", onClick: () => updateStatus(order, "Processing") },
                { key: "Ready", label: "Mark as ready", onClick: () => updateStatus(order, "Ready") },
                { key: "Completed", label: "Mark as completed", onClick: () => updateStatus(order, "Completed") },
                { key: "Rejected", label: "Mark as rejected", danger: true, onClick: () => updateStatus(order, "Rejected") },
              ]}
            />
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- these handlers only call the API and stable setters
    [hotelId] // include hotelId dependency
  );

  const tabs = [
    { key: "pending", label: "Pending" },
    { key: "accepted", label: "Accepted" },
    { key: "processing", label: "Processing" },
    { key: "rejected", label: "Rejected" },
    { key: "completed", label: "Completed" },
  ];

  if (!hotelId) {
    return (
      <div className="hms-page ro-page">
        <Alert
          type="error"
          showIcon
          message="Hotel ID Missing"
          description="Hotel ID was not found in auth data. Please sign in again."
        />
      </div>
    );
  }

  return (
    <div className="hms-page ro-page">
      <PageHeader
        actions={
          <Button icon={<IconRefresh />} onClick={() => refreshTab()} loading={loading}>
            Refresh
          </Button>
        }
      />

      <div className="hms-card is-flush">
        <TabBar tabs={tabs} value={activeKey} onChange={onTabChange} />

        {errorMsg ? <Alert type="error" showIcon message={errorMsg} style={{ margin: 16 }} /> : null}

        <Table
          className="hms-table"
          rowKey="orderId"
          dataSource={orders}
          columns={columns}
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={tablePagination("order")}
        />

        {/* Optional: sample invoice when on Pending */}
        {activeKey === "pending" && orders.length > 0 && (
          <CheckOutInvoice
            isOpen={false}
            onClose={() => {}}
            guestDetails={{ roomNumber: orders[0].roomNumber }}
            hotelDetails={{
              hotelName: "Restaurant Name",
              address: "Restaurant Address",
              phoneNumber: "+000000000",
            }}
            billingRecords={orders}
          />
        )}
      </div>

      {/* Edit Order Modal */}
      {isEditOrderModalOpen && orderToEdit && (
        <EditOrderModal
          isOpen={isEditOrderModalOpen}
          order={orderToEdit}
          hotelId={hotelId}
          onClose={() => setIsEditOrderModalOpen(false)}
          onOrderUpdated={() => refreshTab()}
        />
      )}

      {/* Reserve Table Modal */}
      <ReserveTableModal
        open={isReserveOpen}
        order={orderToReserve}
        tableRef={reserveTableRef}
        setTableRef={setReserveTableRef}
        onCancel={() => {
          setIsReserveOpen(false);
          setOrderToReserve(null);
          setReserveTableRef("");
        }}
        onSubmit={submitReserve}
        submitting={reserveSubmitting}
      />

      {/* Reject Order Modal */}
      <RejectOrderModal
        open={isRejectOpen}
        order={orderToReject}
        reason={rejectReason}
        setReason={setRejectReason}
        onCancel={() => {
          setIsRejectOpen(false);
          setOrderToReject(null);
          setRejectReason("");
        }}
        onSubmit={submitReject}
        submitting={rejectSubmitting}
      />
    </div>
  );
};

export default RestaurantOrderPage;